import { Row, Col } from "antd";
import { useNavigate } from "react-router-dom";
import DateTime from "/app/src/components/generic/formatting/dateTime";
import { workflowService, reportService } from "/app/src/services";
import Controls from "./controls";
import Triggers from "/app/src/components/generic/components/trigger";
import Actions from "./actions";
import Test from "./testWorkflow";
import Delete from "./deleteWorkflow";
import Criterias from "./criteria";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Workflow as WorkflowType,
  Trigger as TriggerType,
} from "/app/src/models";
import { handlePromiseError } from "../../helpers/api";
import { Button, Switch } from "@heroui/react";
import DividerWithText from "/app/src/components/HeroUi/DividerWithText";
import { useCallback } from "react";
import Box from "/app/src/components/generic/components/box";

/**
 * SingleWorkflowView displays the details of a workflow and provides controls for updating it.
 * @param {object} props - The properties object.
 * @param {WorkflowType} props.workflow - The workflow data to display and manage.
 * @returns {JSX.Element} The rendered workflow view component.
 */
export default function SingleWorkflowView({
  workflow,
}: {
  workflow: WorkflowType;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: reports, isFetching: isFetchingReports } = useQuery({
    queryKey: ["reports"],
    queryFn: () => {
      return reportService.getAll();
    },
    initialData: { reports: [] },
    select: (data: { reports: [] }) => {
      return data.reports;
    },
  });

  const { mutateAsync: updateWorkflow } = useMutation({
    mutationFn: (updatedWorkflow: Omit<WorkflowType, "trigger">) => {
      return workflowService
        .updateSingle(workflow.id, updatedWorkflow)
        .then(handlePromiseError);
    },
    onSuccess: (data) => {
      queryClient.setQueryData(["workflow", workflow.id], data);
    },
  });

  /**
   * Toggles the workflow status between active and inactive.
   * @param {boolean} value - A boolean indicating whether the workflow should be active.
   */
  const toggleWorkflowStatus = useCallback(
    (value: boolean) => {
      updateWorkflow({ status: value ? 1 : 0 });
    },
    [updateWorkflow],
  );

  /**
   * Navigates to the logs view for the current workflow.
   */
  const viewLogs = useCallback(() => {
    navigate(`/workflows/${workflow.id}/logs`);
  }, [navigate, workflow.id]);

  /**
   * Updates the workflow's trigger with the provided trigger data.
   * @param {TriggerType} trigger - The new trigger value to update in the workflow.
   */
  const handleTriggerUpdate = useCallback(
    (trigger: TriggerType) => {
      //update nested state property
      const workflowCopy = { ...workflow };
      workflowCopy.trigger = trigger;
      queryClient.setQueryData(["workflow", workflow.id], {
        workflow: workflowCopy,
      });
    },
    [queryClient, workflow],
  );
  return (
    <div className="workflow">
      <Controls workflow={workflow} />
      <Row gutter={20}>
        <Col span={17}>
          <Box>
            <h2>{t("translation:trigger")}</h2>
            <Triggers object={workflow} updateTrigger={handleTriggerUpdate} />
            <DividerWithText>{t("translation:if")}</DividerWithText>
            <Criterias
              workflow={workflow}
              reports={reports}
              isFetchingReports={isFetchingReports}
            />
            <div className="mt-6">
              <DividerWithText>{t("translation:then")}</DividerWithText>
            </div>

            <Actions workflow={workflow} type={"0"} reports={reports} />
            <DividerWithText>{t("translation:else")}</DividerWithText>
            <Actions workflow={workflow} type={"1"} reports={reports} />
          </Box>
        </Col>
        <Col span={7}>
          <Box>
            <Row>
              <Col span={6}>
                <h4>{t("translation:active")}</h4>
                <Switch
                  defaultSelected
                  isDisabled={false}
                  isSelected={workflow.status === 1}
                  onValueChange={toggleWorkflowStatus}
                  aria-label="Workflow active status"
                />
              </Col>
              <Col span={18}>
                <h4>{t("translation:next_trigger")}</h4>
                {workflow.status === 1 ? (
                  workflow.trigger?.nextTrigger ? (
                    <DateTime date={workflow.trigger.nextTrigger} />
                  ) : (
                    <p>{t("translation:select_trigger")}</p>
                  )
                ) : (
                  <p>{t("translation:na")}</p>
                )}
              </Col>
            </Row>
          </Box>
          <Test workflowId={workflow.id} />
          <Delete workflowId={workflow.id} />
          <Button
            fullWidth
            radius="none"
            color="primary"
            size="sm"
            onPress={viewLogs}
          >
            {t("translation:view")} {t("translation:logs")}
          </Button>
        </Col>
      </Row>
    </div>
  );
}
